// ============================================================================
// UK TAX CALCULATION - Income Tax & National Insurance 2024/2025
// Source: HMRC
// ============================================================================

import { TaxCalculationResult, CountryTaxData, DeductionBreakdown } from "./types";
import { calculateTaxByBrackets } from "./utils";

export const UK_TAX_CONFIG: CountryTaxData = {
  socialSecurityRate: 8, // National Insurance Class 1 (reduced from 12% in 2024)
  taxBrackets: [
    { min: 0, max: 12570, rate: 0 }, // Personal allowance
    { min: 12570, max: 50270, rate: 20 }, // Basic rate
    { min: 50270, max: 125140, rate: 40 }, // Higher rate
    { min: 125140, max: Infinity, rate: 45 }, // Additional rate
  ],
  personalAllowance: 12570,
  marriedAllowance: 1260, // Marriage allowance (transfer to spouse)
  childAllowances: [0, 0, 0], // Child benefit handled separately
  currency: "GBP",
};

// National Insurance thresholds 2024/25
const NI_THRESHOLDS = {
  primaryThreshold: 12570, // Annual primary threshold
  upperEarningsLimit: 50270, // Annual upper earnings limit
  lowerRate: 8, // Rate between primary threshold and UEL
  upperRate: 2, // Rate above UEL
};

export interface UKTaxParams {
  grossIncome: number;
  familyStatus: string;
  dependents: number;
}

/**
 * Calcula el Income Tax y National Insurance del Reino Unido
 * 
 * Incluye:
 * - Income Tax (tramos progresivos con Personal Allowance)
 * - National Insurance Class 1 (empleado)
 * - Personal Allowance taper (£100k+)
 */
export function calculateUKTax({
  grossIncome,
  familyStatus,
  dependents,
}: UKTaxParams): TaxCalculationResult {
  const config = UK_TAX_CONFIG;

  // National Insurance
  let nationalInsurance = 0;
  if (grossIncome > NI_THRESHOLDS.primaryThreshold) {
    // NI at lower rate (8%) up to upper earnings limit
    const lowerBandIncome = Math.min(grossIncome, NI_THRESHOLDS.upperEarningsLimit) - NI_THRESHOLDS.primaryThreshold;
    nationalInsurance += (lowerBandIncome * NI_THRESHOLDS.lowerRate) / 100;

    // NI at upper rate (2%) above upper earnings limit
    if (grossIncome > NI_THRESHOLDS.upperEarningsLimit) {
      const upperBandIncome = grossIncome - NI_THRESHOLDS.upperEarningsLimit;
      nationalInsurance += (upperBandIncome * NI_THRESHOLDS.upperRate) / 100;
    }
  }

  // Personal Allowance - tapers for income over £100,000
  // Reduced by £1 for every £2 over £100,000
  let personalAllowance = config.personalAllowance;
  if (grossIncome > 100000) {
    const reduction = Math.floor((grossIncome - 100000) / 2);
    personalAllowance = Math.max(0, personalAllowance - reduction);
  }

  // Marriage Allowance (if applicable)
  let totalAllowance = personalAllowance;
  if (familyStatus === "married" && grossIncome <= 50270) {
    // Can receive up to £1,260 from spouse's allowance
    totalAllowance += config.marriedAllowance;
  }

  // Taxable Income
  const taxableIncome = Math.max(0, grossIncome - totalAllowance);

  // Income Tax using adjusted brackets (since we've already applied allowance)
  const adjustedBrackets = [
    { min: 0, max: 37700, rate: 20 }, // Basic rate (50270 - 12570)
    { min: 37700, max: 112570, rate: 40 }, // Higher rate (125140 - 12570)
    { min: 112570, max: Infinity, rate: 45 }, // Additional rate
  ];

  const incomeTax = calculateTaxByBrackets(taxableIncome, adjustedBrackets);

  const taxRate = grossIncome > 0 ? (incomeTax / grossIncome) * 100 : 0;

  // Build breakdown with UK labels
  const breakdown: DeductionBreakdown[] = [];

  if (incomeTax > 0) {
    breakdown.push({
      id: "income_tax",
      amount: incomeTax,
      rate: taxRate,
      type: "tax",
    });
  }

  if (nationalInsurance > 0) {
    breakdown.push({
      id: "national_insurance",
      amount: nationalInsurance,
      rate: grossIncome > 0 ? (nationalInsurance / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  return {
    tax: incomeTax,
    taxRate,
    socialSecurity: nationalInsurance,
    allowances: totalAllowance,
    breakdown,
  };
}
